import React, { useEffect, useRef } from 'react';

// Hooks
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Actions
import { exchangeHandoffCode } from '~/redux/actions/auth-actions';

// Utils
import {
  isTrustedRedirect,
  sanitizeInternalPath,
  setPostLoginPath,
} from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';

// Components
import { Box, CircularProgress } from '@mui/material';

/**
 * Landing page for logins handed off from another trusted app:
 * /auth/callback?code=...&redirect_path=/campaigns&failure_redirect_uri=https://other.app/login
 */
const AuthCallback = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects; a code can only be exchanged once
    if (hasRun.current) return;
    hasRun.current = true;

    const code = searchParams.get('code');
    const redirectPath = sanitizeInternalPath(searchParams.get('redirect_path'));
    const failureRedirectUri = searchParams.get('failure_redirect_uri');

    const fail = (error, message) => {
      if (failureRedirectUri && isTrustedRedirect(failureRedirectUri)) {
        const url = new URL(failureRedirectUri);
        url.searchParams.set('error', error);
        window.location.replace(url.toString());
        return;
      }
      window.location.replace(
        `/login?success=false&message=${encodeURIComponent(message || MESSAGES.GENERAL_ERROR)}`
      );
    };

    if (!code) {
      fail('missing_code');
      return;
    }

    dispatch(exchangeHandoffCode(code)).then((result) => {
      if (result?.message === 'success') {
        // Full reload so App re-reads the new token from localStorage
        window.location.replace(redirectPath);
      } else if (result?.message === '2fa_required' || result?.message === '2fa_setup_required') {
        // The global 2FA modal is now open on top of this page; it sends the user to this path
        // once the code is verified (or back to /login if they cancel)
        setPostLoginPath(redirectPath);
      } else {
        fail('invalid_code', result?.response?.data?.message);
      }
    });
  }, []);

  return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
      <CircularProgress />
    </Box>
  );
};

export default AuthCallback;
